<template>
    <div>
        <b-container fluid>
            <div class="my-3">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="h3 m-0 mr-3">
                        <span class="ml-3">Articles</span>
                        <span
                            id="number-expired-btn"
                            v-if="expired_articles.length > 0"
                            class="ml-5 mb-1 btn btn-danger text-white"
                            @click="viewExpired">
                            {{ expired_articles.length }}
                        </span>
                        <b-tooltip target="number-expired-btn" triggers="hover" v-if="expired_articles.length > 0">
                            {{ expired_articles.length }} Articles have expired. (Click to view)
                        </b-tooltip>
                    </div>
                </div>
            </div>

            <b-form @submit.prevent="search">
                <div class="d-flex align-items-center mb-3">
                    <div class="flex-grow-1">
                        <b-input-group>
                            <b-form-input ref="search-term" type="search" v-model="search_term" placeholder="Search" :disabled="!can_search_articles" />
                            <b-input-group-append>
                                <b-btn
                                    variant="outline-secondary"
                                    :disabled="loading_articles || !can_search_articles"
                                    @click="loadLatestArticles"
                                >
                                    Latest
                                </b-btn>
                                <b-btn type="submit" variant="outline-secondary" :disabled="loading_articles || !can_search_articles">
                                    <font-awesome-icon :icon="['fad', 'search']" />
                                </b-btn>
                            </b-input-group-append>
                            <b-input-group-append>
                                <b-btn
                                    id="article-search-info"
                                    variant="light"
                                    :disabled="!can_search_articles"
                                >
                                    <font-awesome-icon :icon="['fad', 'info-circle']" />
                                </b-btn>
                                <b-tooltip
                                    target="article-search-info"
                                    triggers="hover focus"
                                    placement="bottom"
                                >
                                    Use AND, OR, NOT, and quotes for exact phrases. Use * for prefix wildcard
                                    (e.g. educat*). Example: "civil rights" AND education NOT war. Fuzzy search
                                    is on by default.
                                </b-tooltip>
                            </b-input-group-append>
                                <div class="d-flex">
                                    <div class="mx-2">
                                        <b-btn
                                            variant="light"
                                            :class="filters_visible ? 'active' : 'collapsed'"
                                            @click="filters_visible = !filters_visible"
                                            :disabled="!can_search_articles"
                                        >
                                            <font-awesome-icon :icon="['fad', 'filter']" />
                                        </b-btn>
                                    </div>
                                </div>
                        </b-input-group>
                    </div>
                </div>
                <b-collapse
                    id="filters"
                    v-model="filters_visible"
                >
                    <div v-if="filters_visible" class="mb-3">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <div class="row align-items-center">
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.tags"
                                            :options="available_tags"
                                            multiple
                                            label="tag"
                                            track-by="tag"
                                            placeholder="Filter By Tags"
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <language-multiselect
                                            :select_label="select_label"
                                            :deselect_label="deselect_label"
                                            v-model="filters.languages"
                                            :options="available_languages"
                                            @add="addLanguage"
                                            @remove="removeLanguage"
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.types"
                                            :options="types"
                                            multiple
                                            label="name"
                                            track-by="name"
                                            placeholder="Filter By Type"
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <div v-if="hide_archived" class="btn btn-primary" @click="hide_archived = false"> Show Archived </div>
                                        <div v-if="!hide_archived" class="btn btn-warning" @click="hide_archived = true"> Hide Archived </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="active_filters" class="ml-3">
                                <b-btn variant="danger" @click="clearFilters">
                                    <font-awesome-icon :icon="['fad', 'backspace']" /> Clear All Filters
                                </b-btn>
                            </div>
                        </div>
                    </div>
                </b-collapse>
            </b-form>

            <b-alert
                v-if="showListBanner"
                :variant="listBannerVariant"
                show
                class="mb-3"
            >
                {{ listBannerText }}
            </b-alert>

            <div v-if="loading_articles" class="mb-3">
                <b-spinner label="Spinning"></b-spinner>
            </div>

            <div v-else>
                <b-table
                    v-if="filteredArticles"
                    primary-key="id"
                    ref="articles-table"
                    striped
                    responsive
                    sticky-header
                    outlined
                    no-border-collapse
                    head-variant="light"
                    :busy="loading_articles"
                    :items="pagedArticles"
                    :fields="fields"
                >

                    <template #table-busy>
                        <b-spinner class="align-middle"></b-spinner>
                    </template>

                    <template #cell(title)="data">
                        <a
                            v-if="data.item && data.item.title"
                            class="cursor-pointer"
                            :class="hasBeenViewed(data.item.id) ? 'viewed' : ''"
                            @click="openArticlePreview(data.item.id)"
                            >
                            {{ data.item.title }}
                        </a>
                        <a
                            v-else
                            class="cursor-pointer"
                            :class="hasBeenViewed(data.item.id) ? 'viewed' : 'not-viewed-no-title'"
                            @click="openArticlePreview(data.item.id)"
                        >
                            no title
                        </a>
                    </template>
                    <template #cell(viewed)="data">
                        <b-form-checkbox
                            class="text-center"
                            size="sm"
                            :checked="hasBeenViewed(data.item.id)"
                            @change="updateViewedArticles(data.item.id)"
                            :disabled="updating_viewed_status"
                            switch
                        >
                        </b-form-checkbox>
                    </template>
                    <template #cell(lexile)="data">
                        <div>
                            <span v-if="data.item.lexile_api_score">
                                {{ data.item.lexile_api_score }}
                            </span>
                            <span v-else="data.item.lexile_api_score">
                                N/A
                            </span>
                        </div>
                    </template>
                    <template #cell(language)="data">
                        <div class="d-flex align-items-center justify-content-center text-uppercase">
                            <div class="mr-1">
                                <span v-if="data.item.language == 'en' || data.item.language == 'us'">
                                    <flag iso="us" />
                                </span>
                                <span v-if="data.item.language == 'es' || data.item.language == 'sp'">
                                    <flag iso="es" />
                                </span>
                            </div>
                            <div v-if="data.item.language">
                                {{data.item.language}}
                            </div>
                            <div v-else>
                                N/A
                            </div>
                        </div>
                    </template>
                    <template #cell(tags)="data">
                        <div class="d-flex align-items-center justify-content-center">
                            <div class="mr-1">
                                <span v-if="data.item.tag_names">
                                    <span
                                        v-for="(tag, i) in splitTags(data.item.tag_names)"
                                        :key="i"
                                        class="badge badge-primary m-2 p-2"
                                        >
                                        {{ tag }}
                                    </span>
                                </span>
                                <span v-else >
                                    None
                                </span>
                            </div>
                        </div>
                    </template>
                    <template #cell(pedigree_count)="data">
                        <b-btn :id="`popover-${data.item.id}`" variant="primary" @click="handlePedigree(data.item)" >{{ parseInt(data.item.pedigree_count) }}</b-btn>
                        <div>
                            <b-popover :show.sync="data.item.id == openPopoverId" :target="`popover-${data.item.id}`" @blur="togglePopover(data.item.id)" triggers="click blur" placement="left" class="mt-2">
                                <template #title>
                                    <b-btn @click="togglePopover(data.item.id)" class="close" aria-label="Close">
                                        <span class="d-inline-block" aria-hidden="true">&times;</span>
                                    </b-btn>
                                    Pedigree Chart
                                </template>
                                <div v-if="loading_pedigree_data" class="text-dark mr-1">
                                    <b-spinner small label="Loading..."></b-spinner>
                                </div>
                                <div v-else>
                                    <b-alert
                                        variant="danger"
                                        :show="!pedigree_chart || pedigree_chart && !pedigree_chart.length"
                                        class="my-2"
                                    >
                                        No Pedigree Found
                                    </b-alert>

                                    <div class="mt-2">
                                        <div class="pedigree_container">
                                            <b-list-group>
                                                <b-list-group-item v-for="(item, index) in pedigree_chart" :key="index">
                                                    <div class="d-flex align-items-center">
                                                        <div>
                                                            <div class="text-muted">
                                                                <small>Publication</small>
                                                            </div>
                                                            <div v-if="item.publication.id">
                                                                <router-link :to="{path: '/publications/' + item.publication.id}" target="_blank">
                                                                    {{item.publication.sku}}
                                                                </router-link>
                                                            </div>
                                                            <div v-else>
                                                                {{item.publication.sku}}
                                                            </div>
                                                        </div>
                                                        <div class="mx-3 text-muted">
                                                            /
                                                        </div>
                                                        <div>
                                                            <div class="text-muted">
                                                                <small>Week</small>
                                                            </div>
                                                            <div class="d-flex align-items-center">
                                                                <div class="mr-1">
                                                                    <span>{{item.order_number}}.</span>
                                                                </div>
                                                                <div v-if="item.publication.id">
                                                                    <router-link :to="{path: '/publications/' + item.publication.id, query: { unit_id: item.id }}" target="_blank">
                                                                        <span v-if="item.title">{{item.title}}</span><span v-else class="text-muted">no title</span>
                                                                    </router-link>
                                                                </div>
                                                                <div v-else>
                                                                    <span v-if="item.title">{{item.title}}</span><span v-else class="text-muted">no title</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div class="mx-3 text-muted">
                                                            /
                                                        </div>
                                                        <div>
                                                            <div>
                                                                <div class="text-muted">
                                                                    <small>Article</small>
                                                                </div>
                                                                <router-link :to="{path: '/publications/' + item.publication.id, query: { unit_id: item.id, article_id: data.item.id  }}" target="_blank">
                                                                    <span v-if="data.item.title">{{data.item.title}}</span><span v-else class="text-muted">no title</span>
                                                                </router-link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </b-list-group-item>
                                            </b-list-group>
                                        </div>
                                    </div>
                                </div>
                            </b-popover>
                        </div>

                    </template>
                    <template #cell(expiration_date)="data">
                        <div v-if="data.item.expiration_date" class="badge p-2" :class="data.item.expired != '1' ? 'badge-primary' : 'badge-danger'">
                            {{ new Date(data.item.expiration_date).toLocaleDateString('en-US') }}
                        </div>
                    </template>
                    <template #cell(archived)="data">
                        <b-form-checkbox
                            class="text-center"
                            size="sm"
                            :checked="hasBeenArchived(data.item.id)"
                            @change="updateArchived(data.item.id)"
                            :disabled="updating_viewed_status"
                            switch
                        >
                        </b-form-checkbox>
                    </template>
                </b-table>

                <b-alert
                    variant="danger"
                    :show="searched && (!filteredArticles || filteredArticles && !filteredArticles.length)"
                >
                    0 results
                </b-alert>

                <div v-if="filteredArticles && filteredArticles.length" class="d-flex align-items-center justify-content-between mt-3">
                    <div class="d-flex align-items-center">
                        <span class="mr-2">Per page</span>
                        <b-form-select
                            v-model="per_page"
                            :options="per_page_options"
                            size="sm"
                        />
                    </div>
                    <b-pagination
                        v-model="current_page"
                        :total-rows="filteredArticles.length"
                        :per-page="per_page"
                        size="sm"
                        align="right"
                    />
                </div>
            </div>
        </b-container>

        <article-modal
            v-model="show_article_modal"
            :article_id="selected_article_id"
            @hide="show_article_modal = false"
            @updatedExpiration="updateExpirationDate"
            @updateSearch="search"
        />

    </div>
</template>

<style lang="scss" scoped>

.b-table-sticky-header {
    max-height: calc(100vh - 250px) !important;
    min-height: 0 !important;
}
.cursor-pointer {
    cursor: pointer;
}

.close {
    transform: translateY(-5px);
}
.close:hover {
    background-color: unset !important;
}
.pedigree_container {
    max-height: 476px;
    overflow-y: auto;
}

.viewed{
    color: purple;
}

.not-viewed-no-title{
    color: gray;
}
</style>

<script>
import ArticleModal from '@/components/modals/article/ArticleModal'
import LanguageMultiselect from '@/components/LanguageMultiselect'
import { mapState, mapActions } from 'vuex';
import stripHtml from 'string-strip-html'
import types from '@/common/article_types'
import listBannerMixin from '@/common/list_banner_mixin'

export default {
    mixins: [listBannerMixin],
    data: () => ({
        filters: {tags: [], languages: [], types: []},
        viewed_articles: [],
        updating_viewed_status: false,
        fields: [
            {
                key: 'id',
                sortable: true,
                thStyle: { width: "5%" },
            },
            {
                key: 'title',
                sortable: true,
                thStyle: { width: "45%" },
            },
            {
                key: 'viewed',
                sortable: true,
                thStyle: { width: "5%" },
            },
            {
                key: 'body',
                label: 'Word Count',
                sortable: true,
                sortByFormatted: true,
                formatter: 'wordCount',
                thStyle: { width: "5%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'lexile_api_score',
                label: 'Lexile',
                sortable: true,
                thStyle: { width: "5%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'language',
                sortable: true,
                thStyle: { width: "10%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'pedigree_count',
                label: 'Pedigree Count',
                sortable: true,
                thStyle: { width: "5%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'expiration_date',
                label: 'Expiration Date',
                sortable: true,
                thStyle: { width: "5%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'tags',
                label: 'Tags',
                sortable: true,
                thStyle: { width: "5%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'type',
                sortable: true,
                thStyle: { width: "5%"},
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'archived',
                sortable: true,
                thStyle: { width: "5%" },
            },
        ],
        types: types,
        loading_articles: false,

        articles: [],
        pedigree_chart: [],

        article: {},
        selected_article_id: null,

        search_term: '',

        searched: false,

        show_article_modal: false,
        select_label: '',
        deselect_label: 'remove',
        filters_visible: false,
        loading_pedigree_data: false,
        openPopoverId: null,
        hide_archived: true,
        expired_articles: [],
        active_list: 'latest',
        current_page: 1,
        per_page: 25,
        per_page_options: [10, 25, 50, 100],
    }),
    props: [],
    computed: {
        ...mapState({
            errors: state => state.errors.errors,
            user: state => state.user.user,
            tags: state => state.tags.tags,
        }),
        can_search_articles() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'search_article')
        },
        filteredArticles () {
            return this.articles.filter(article => {
                let archived_match = false
                if(article.archived == 0 || (article.archived == 1 && this.hide_archived == false)){
                    archived_match = true;
                }

                return archived_match
            })
        },
        pagedArticles () {
            if (!this.filteredArticles || !this.filteredArticles.length) {
                return []
            }
            const start = (this.current_page - 1) * this.per_page
            const end = start + this.per_page
            return this.filteredArticles.slice(start, end)
        },
        active_filters () {
            let active_filters = false
            let f = this.filters
            if (f.tags.length || f.languages.length || f.types.length) {
                active_filters = true
            }
            return active_filters
        },
        available_languages () {
            let languages = []

            _.forEach(this.articles, article => {
                languages.push({
                    language: article.language
                })
            })

            languages = _.orderBy(languages, 'language')
            languages = _.uniqBy(languages, 'language')

            return languages
        },
        available_tags () {
            let tags = [];
            _.forEach(this.articles, article => {
                let tag_names = _.split(article.tag_names, ',');
                _.forEach(tag_names, tag_name => {
                    if(tag_name && !tags.includes(tag_name)){
                        tags.push({
                            tag: tag_name
                        })
                    }
                })
            })
            tags = _.orderBy(tags, 'tag')
            tags = _.uniqBy(tags, 'tag')

            return tags
        },
        bannerMode() {
            return this.active_list
        },
        bannerSearchTerm() {
            return this.search_term
        },
        bannerItemLabel() {
            return 'articles'
        }
    },
    components: {
        ArticleModal,
        LanguageMultiselect
    },
    localStorage: {
        article_search_term: {
            type: String
        },
        articles: {
            type: Array
        }
    },
    watch: {
        active_filters: {
            handler (active_filters) {
                if (active_filters) {
                    this.filters_visible = true
                }
            },
            immediate: true
        },
        per_page () {
            this.current_page = 1
        },
        filteredArticles (articles) {
            const totalPages = Math.max(1, Math.ceil((articles || []).length / this.per_page))
            if (this.current_page > totalPages) {
                this.current_page = 1
            }
        },
        filters: {
            deep: true,
            handler () {
                this.handleFilterChange()
            },
        },
    },
    methods: {
        buildSearchParams () {
            return {
                tags: this.filters.tags.map(tag => tag.tag),
                languages: this.filters.languages.map(language => {
                    return language.language ? language.language : '__EMPTY__'
                }),
                types: this.filters.types.map(type => type.name),
                tag_mode: 'or',
            }
        },
        formatArticleType (article) {
            if (article.is_activity) {
                return 'Activity'
            }

            if (article.is_walkthrough) {
                return 'Walkthrough'
            }

            if (article.is_rubric) {
                return 'Rubric'
            }

            return 'Article'
        },
        mapArticles (articles) {
            return (articles || []).map(article => {
                return {
                    ...article,
                    type: this.formatArticleType(article),
                }
            })
        },
        handlePedigree(article) {
            if (article.id === this.openPopoverId) {
                this.togglePopover(article.id)
            } else {
                this.showPedigree(article.id)
            }
        },
        showPedigree(article_id) {
            this.togglePopover(article_id)
            this.loading_pedigree_data = true
            this.$store.dispatch('getArticles', article_id)
            .then(response => {
                this.pedigree_chart = _.orderBy(response.data[0].units, ['publication.id', 'order_number'])
                this.loading_pedigree_data = false
            })
        },
        togglePopover(id) {
            if (this.openPopoverId === id) {
                // Clicked on the currently open popover, close it
                this.openPopoverId = null;
            } else {
                // Clicked on a new popover, close the currently open popover (if any)
                this.openPopoverId = id;
            }
        },
        search () {
            this.active_list = 'searched';
            this.loading_articles = true
            this.searched = false
            this.openPopoverId = null;
            this.current_page = 1;

            this.axios.get('/api/articles/search_typesense/' + encodeURIComponent(this.search_term), {
                params: this.buildSearchParams(),
            })
            .then(response => {
                if (response.data) {
                    this.articles = this.mapArticles(response.data)

                    this.loading_articles = false
                    this.searched = true
                }
            })
            .catch(error => {
                if (error) {
                    this.loading_articles = false
                    this.searched = false
                }
            })
        },
        async loadLatestArticles() {
            this.active_list = 'latest';
            this.loading_articles = true;
            this.searched = false;
            this.openPopoverId = null;

            try {
                const response = await this.axios.get('/api/articles/latest')
                if (response.data) {
                    this.articles = this.mapArticles(response.data);
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to get latest articles: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.loading_articles = false;
            }
        },
        clearSearch () {
            this.filteredArticles = []

            this.$localStorage.set('articles', [])

            this.search_term = ''

            this.$localStorage.set('article_search_term', '')

            if (this.$refs['search-term']) {
                this.$refs['search-term'].focus()
            }
        },
        clearFilters() {
            this.filters = {tags: [], languages: [], types: []}
        },
        handleFilterChange () {
            this.current_page = 1

            if (this.active_list !== 'searched' || !this.search_term || !this.search_term.trim()) {
                return
            }

            this.search()
        },
        openArticlePreview (article_id) {
            this.selected_article_id = article_id
            this.show_article_modal = true
            this.viewed_articles.push(article_id)
        },
        editArticle (article) {
            this.article = _.cloneDeep(article)

            this.show_article_modal = true
        },
        removeTags(input) {
            if (input === null || input === undefined) {
                return ''; // or handle the case appropriately
            }
            return input
                .replace(/<\/p><p>(?=\w)/g, '</p> <p>') // Add space between <p> tags with a word character after the closing tag
                .replace(/<\/?[^>]+(>|$)/g, ''); // Remove other HTML tags
        },
        wordCount (string) {
            if (!string) {
                return 'N/A'
            }
            string = this.removeTags(string)
            let words = null

            if (string) {
                words = stripHtml(string).result

                words = words.split(' ').filter((n) => { return n != '' }).length
            }

            return parseInt(words)
        },
        handleScroll() {
            // Check if the user has scrolled to the bottom of the table container
            const tableContainer = this.$refs.tableContainer;
            const isAtBottom = tableContainer.scrollHeight - tableContainer.scrollTop <= tableContainer.clientHeight;

            if (isAtBottom) {
                // Load more records when the user reaches the bottom
                this.loadMoreRecords();
            }
        },
        loadMoreRecords() {
            this.visibleRecordCount += this.recordsPerLoad;
        },
        addLanguage (new_language) {
            this.filters.languages.push(new_language)
        },
        removeLanguage (language) {
            let index = _.findIndex(this.filters.languages, {language: language.language})
            this.filters.languages.splice(index, 1)
        },
        hasBeenViewed(article_id){
            return this.viewed_articles.includes(article_id);
        },
        updateViewedArticles(article_id){
            this.updating_viewed_status = true;
            const index = this.viewed_articles.indexOf(article_id);
            if(index !== -1){
                this.viewed_articles.splice(index, 1);
                this.updating_viewed_status = false;
            }
            else{
                this.viewed_articles.push(article_id);
                this.updating_viewed_status = false;
            }
        },
        hasBeenArchived(article_id){
            let checking_article = this.articles.find(article => article.id === article_id);
            return checking_article.archived == 1 ? true: false;
        },
        updateArchived(article_id){
            let is_archived = this.hasBeenArchived(article_id)
            is_archived = !is_archived;

            this.axios.post('/api/articles/set_as_archived/' + article_id, {
                article_id: article_id,
                new_archived_status: is_archived
            })
            .then(response => {
                if (response && response.data) {
                    this.articles = this.articles.map(article =>
                        article.id === response.data.id
                            ? Object.assign({}, article, response.data)  // Merging objects
                            : article
                    );
                    const index = this.articles.findIndex(a => a.id === response.data.id);
                    if (index !== -1) {
                        this.$set(this.articles, index, Object.assign({}, this.articles[index], response.data));
                    }
                }
            })
        },
        splitTags(csv) {
            if (!csv) return [];
            return csv
                .split(',')               // split on commas
                .map(t => t.trim())       // trim spaces
                .filter(Boolean);         // drop empties
        },
        async getExpiredArticles(refresh_list=false){
            try {
                await this.axios.get('/api/articles/get_expired_articles')
                .then(response => {
                    if (response.data) {
                        this.expired_articles = response.data;
                        if(refresh_list){
                            this.articles = response.data;
                        }
                    }
                })
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to get expired articles: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } 
        },
        viewExpired(){
            this.active_list = 'expired';
            this.articles = this.expired_articles
        },
        updateExpirationDate(){
            if(this.active_list == 'searched'){
                this.search();
            }
            else{
                this.getExpiredArticles(true);
            }   
        }
    },
    async mounted () {
        this.loadLatestArticles();
        this.getExpiredArticles();
        await this.$store.dispatch('getTags');
    },
}
</script>
